import React, { useState, useEffect } from 'react';
import { TextField, Button, Alert, Box, CircularProgress, Typography } from '@mui/material';
import { setApiKey, checkApiKey } from '../api/openaiService';
import { useAppContext } from '../contexts/AppContext';

function ApiKeyInput() {
  const { setApiKey: setContextApiKey, setApiKeySet, setError, loading, setLoading } = useAppContext();
  const [inputKey, setInputKey] = useState('');
  const [success, setSuccess] = useState(false);
  const [keySource, setKeySource] = useState('');
  const [checking, setChecking] = useState(true);

  // Check if API key is already set on the server when component mounts
  useEffect(() => {
    const checkForApiKey = async () => {
      try {
        setChecking(true);
        const response = await checkApiKey();
        if (response.keySet) {
          setApiKeySet(true);
          setSuccess(true);
          setKeySource(response.source);
        }
      } catch (error) {
        console.error('Something went wrong');
      } finally {
        setChecking(false);
      }
    };

    checkForApiKey();
  }, [setApiKeySet]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputKey.trim()) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await setApiKey(inputKey);
      // If no error was thrown, consider it successful
      setContextApiKey(inputKey);
      setApiKeySet(true);
      setSuccess(true);
      setError(null);
    } catch (error) {
      setError(error?.response?.data?.error || error.message || 'Failed to set API key');
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {checking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>Checking for API key...</Typography>
        </Box>
      ) : success && keySource === 'environment' ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          API key automatically loaded from server environment variables! No need to enter it manually.
        </Alert>
      ) : success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          API key set successfully!
        </Alert>
      )}

      {!success && (
        <>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Enter your OpenAI API key below. This will be used to generate the literature review.
          </Typography>
          <TextField
            fullWidth
            label="OpenAI API Key"
            value={inputKey}
            onChange={(e) => setInputKey(e.target.value)}
            placeholder="sk-..."
            type="password"
            margin="normal"
            variant="outlined"
            required
            disabled={loading || checking}
          />
        </>
      )}

      {!success && (
        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 1 }}
          disabled={!inputKey.trim() || loading || checking}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              Setting API Key...
            </Box>
          ) : (
            'Set API Key'
          )}
        </Button>
      )}
    </Box>
  );
}

export default ApiKeyInput; 